import { useCallback, useEffect, useState } from "react";
import { Globe2, Save, Target, DollarSign, Users, Loader2, Percent } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { getMetaGlobal, upsertMetaGlobal, updateConfig } from "@/lib/api";
import { fmtBRL, fmtInt, fmtPct, pct } from "@/lib/format";

/**
 * Meta global do time — financeira (R$) + clientes (qtd) + bônus coletivo (%).
 * - Admin: edita as duas metas + bônus.
 * - Vendedora/Técnico: só lê.
 */
export default function MetaGlobalCard({
  editable = false,
  teamReceita = 0,
  teamClientes = 0,
  config = null,          // config app (pra ler/editar team_bonus_pct)
  onConfigChange = null,  // callback qdo salvar config
}) {
  const [meta, setMeta] = useState({ meta_mensal: 0, meta_clientes: 0 });
  const [draft, setDraft] = useState({ meta_mensal: "", meta_clientes: "" });
  const [teamBonusDraft, setTeamBonusDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMetaGlobal();
      const m = { meta_mensal: Number(r?.meta_mensal || 0), meta_clientes: Number(r?.meta_clientes || 0) };
      setMeta(m);
      setDraft({ meta_mensal: String(m.meta_mensal || ""), meta_clientes: String(m.meta_clientes || "") });
    } catch (e) {
      console.error("Erro meta global:", e);
      toast.error("Não foi possível carregar a meta global");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Inicializa o bônus do time a partir do config (fração 0..1 → %)
  useEffect(() => {
    if (config && teamBonusDraft === "") {
      setTeamBonusDraft(((Number(config.team_bonus_pct) || 0) * 100).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const patch = {
        meta_mensal: Number(draft.meta_mensal) || 0,
        meta_clientes: Number(draft.meta_clientes) || 0,
      };
      const r = await upsertMetaGlobal(patch);
      setMeta({ meta_mensal: Number(r.meta_mensal || 0), meta_clientes: Number(r.meta_clientes || 0) });
      // Se o bônus do time foi alterado, salva o config também.
      if (editable && config) {
        const newTeamBonus = Math.max(0, Number(teamBonusDraft) || 0) / 100;
        if (Math.abs(newTeamBonus - Number(config.team_bonus_pct || 0)) > 1e-9) {
          const saved = await updateConfig({
            plans: config.plans || [],
            bonus_pct: Number(config.bonus_pct) || 0,
            team_goal: Number(config.team_goal) || 0,
            team_bonus_pct: newTeamBonus,
          });
          onConfigChange && onConfigChange(saved);
        }
      }
      toast.success("Meta global atualizada");
    } catch { toast.error("Erro ao salvar meta global"); }
    finally { setSaving(false); }
  };

  const pReceita = pct(teamReceita, meta.meta_mensal || 1);
  const pClientes = pct(teamClientes, meta.meta_clientes || 1);
  const toneRec = pReceita >= 100 ? "text-emerald-500" : pReceita >= 50 ? "text-amber-500" : "text-red-500";
  const toneCli = pClientes >= 100 ? "text-emerald-500" : pClientes >= 50 ? "text-amber-500" : "text-red-500";
  const teamBonusView = Number((editable ? (Number(teamBonusDraft) || 0) : ((config?.team_bonus_pct || 0) * 100)).toFixed(2));

  return (
    <div className="rounded-xl border border-border bg-card p-5 card-flua" data-testid="meta-global-card">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <h3 className="font-heading font-semibold text-lg">Meta global do time</h3>
        </div>
        {editable && <Badge variant="outline" className="text-[10px]">Admin</Badge>}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Financeira */}
          <div className="rounded-lg border border-border p-4" data-testid="meta-global-financeira">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
                <DollarSign className="w-3.5 h-3.5 text-emerald-500" strokeWidth={1.5} /> Financeira
              </div>
              <div className={`font-heading text-xl font-extrabold tabular-nums ${toneRec}`}>{fmtPct(pReceita)}</div>
            </div>
            <Progress value={Math.min(pReceita, 100)} className="h-2 mb-2" />
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-mono">{fmtBRL(teamReceita)}</span> de <span className="text-foreground font-mono">{fmtBRL(meta.meta_mensal)}</span>
            </div>
            {editable && (
              <div className="mt-3">
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Meta global (R$)</label>
                <Input type="number" min="0" step="0.01"
                  value={draft.meta_mensal}
                  onChange={(e) => setDraft((p) => ({ ...p, meta_mensal: e.target.value }))}
                  data-testid="input-meta-global-mensal" />
              </div>
            )}
          </div>

          {/* Clientes */}
          <div className="rounded-lg border border-border p-4" data-testid="meta-global-clientes">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
                <Users className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} /> Clientes
              </div>
              <div className={`font-heading text-xl font-extrabold tabular-nums ${toneCli}`}>{fmtPct(pClientes)}</div>
            </div>
            <Progress value={Math.min(pClientes, 100)} className="h-2 mb-2" />
            <div className="text-xs text-muted-foreground">
              <span className="text-foreground font-mono">{fmtInt(teamClientes)}</span> de <span className="text-foreground font-mono">{fmtInt(meta.meta_clientes)}</span> clientes
            </div>
            {editable && (
              <div className="mt-3">
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Meta global (clientes)</label>
                <Input type="number" min="0" step="1"
                  value={draft.meta_clientes}
                  onChange={(e) => setDraft((p) => ({ ...p, meta_clientes: e.target.value }))}
                  data-testid="input-meta-global-clientes" />
              </div>
            )}
          </div>

          {/* Bônus coletivo */}
          <div className="rounded-lg border border-border p-4" data-testid="meta-global-bonus">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted-foreground">
                <Percent className="w-3.5 h-3.5 text-amber-500" strokeWidth={1.5} /> Bônus do time
              </div>
              <div className="font-heading text-xl font-extrabold tabular-nums text-amber-500">
                {teamBonusView.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              Aplicado sobre a comissão base de <strong>todo o time</strong> quando bater a meta global.
            </div>
            {editable && (
              <div className="mt-3">
                <label className="text-[10px] text-muted-foreground uppercase tracking-widest">Bônus coletivo (%)</label>
                <Input type="number" min="0" step="0.1"
                  value={teamBonusDraft}
                  onChange={(e) => setTeamBonusDraft(e.target.value)}
                  data-testid="input-meta-global-bonus" />
              </div>
            )}
          </div>
        </div>
      )}

      {editable && !loading && (
        <div className="mt-4 flex justify-end">
          <Button onClick={handleSave} disabled={saving} data-testid="btn-save-meta-global">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar meta global
          </Button>
        </div>
      )}

      {!editable && (
        <p className="text-[11px] text-muted-foreground mt-4 flex items-center gap-1.5">
          <Target className="w-3 h-3" strokeWidth={1.5} /> Meta gerenciada pelo admin. Vá com força — quando o time bate, todo mundo comemora.
        </p>
      )}
    </div>
  );
}
